/** One rendered line of command output. */
export interface OutputLine {
  className?: string;
  text?: string;
  html?: string;
}

export interface Command {
  command: string;
  output: OutputLine[];
}

/**
 * The scripted session that types itself out on the homepage. Content only —
 * the typing, scrolling and search behaviour lives in terminal.ts.
 */
export const COMMANDS: Command[] = [
  {
    command: "neofetch",
    output: [
      { className: "ascii", text: "    ___  ___  ___  _  _" },
      { className: "ascii", text: "  _ | \\ / _ \\/ __|| || |" },
      { className: "ascii", text: " | || || (_) \\__ \\| __ |" },
      { className: "ascii", text: "  \\__/  \\___/|___/|_||_|" },
      { className: "ascii", text: "     _  _   _    __  __ __  __ ___ ___" },
      { className: "ascii", text: "    | || | /_\\  |  \\/  |  \\/  | __| _ \\" },
      { className: "ascii", text: "    | __ |/ _ \\ | |\\/| | |\\/| | _||   /" },
      { className: "ascii", text: "    |_||_/_/ \\_\\|_|  |_|_|  |_|___|_|_\\" },
      { className: "blank" },
      { html: '<span class="tl-key">Host</span><span class="tl-sep">:</span> joshammer.com' },
      { html: '<span class="tl-key">Stack</span><span class="tl-sep">:</span> TypeScript \u00b7 Phaser \u00b7 Node \u00b7 Vite \u00b7 Nix' },
      { html: '<span class="tl-key">Netcode</span><span class="tl-sep">:</span> MessagePack over WebSocket, 60 Hz' },
      { html: '<span class="tl-key">Uptime</span><span class="tl-sep">:</span> <span id="neofetch-uptime">loading...</span>' },
    ],
  },
  {
    command: "typst compile me.typ",
    output: [
      { className: "typ-comment", text: "// me.typ" },
      { className: "blank" },
      { className: "typ", html: '<span class="typ-func">#set</span> <span class="typ-key">text</span>(<span class="typ-key">font</span>: <span class="typ-str">"JetBrains Mono"</span>)' },
      { className: "blank" },
      { className: "typ", html: '<span class="typ-func">#table</span>(' },
      { className: "typ", html: '  <span class="typ-key">columns</span>: <span class="typ-num">2</span>,' },
      { className: "typ", html: '  [<span class="typ-key">Identity</span>], [<span class="typ-str">"Christian"</span>],' },
      { className: "typ", html: '  <span class="typ-comment">// Jesus said, \u201cI am the resurrection and the life.\u201d \u2014 John 11:25</span>' },
      { className: "typ", html: '  [<span class="typ-key">Role</span>],     [<span class="typ-str">"Software Engineer"</span>],' },
      { className: "typ", html: '  [<span class="typ-key">Editor</span>],   [<span class="typ-str">"Neovim"</span>],' },
      { className: "typ", html: '  [<span class="typ-key">OS</span>],       [<span class="typ-str">"NixOS"</span>],' },
      { className: "typ", html: '  [<span class="typ-key">Sport</span>],    [<span class="typ-str">"Ultimate Frisbee"</span>],' },
      { className: "typ", html: ")" },
    ],
  },
  {
    command: "./this_site --info",
    output: [
      { className: "heading", text: "this_site v2.0.0" },
      { className: "blank" },
      { text: "A multiplayer grappling game built from scratch." },
      { className: "blank" },
      { text: "  \u2022 One deterministic simulation, run in two places" },
      { text: "  \u2022 Phaser in the browser, Phaser headless on the server" },
      { text: "  \u2022 Prediction and replay over a MessagePack WebSocket" },
      { text: "  \u2022 Fourteen rooms, a collapsing shaft, passphrase accounts" },
      { text: "  \u2022 Node + Vite, packaged with Nix" },
      { className: "blank" },
      { className: "dim", text: "source: this page + the game floating to the right" },
    ],
  },
  {
    command: "ls -la skills/",
    output: [
      { className: "dir-entry", html: '<span class="de-perm">drwxr-xr-x</span>  <span class="de-dim">- josh josh</span>  <span class="de-name">languages/</span>' },
      { className: "dir-entry", html: '<span class="de-perm">drwxr-xr-x</span>  <span class="de-dim">- josh josh</span>  <span class="de-name">domains/</span>' },
    ],
  },
  {
    command: "ls -1 skills/languages/",
    output: [
      { className: "accent", text: "Python" },
      { className: "accent", text: "Go" },
      { className: "accent", text: "Kotlin" },
      { className: "accent", text: "Java" },
      { className: "accent", text: "Clojure" },
      { className: "accent", text: "Zig" },
      { className: "accent", text: "TypeScript" },
    ],
  },
  {
    command: "ls -1 skills/domains/",
    output: [
      { className: "accent", text: "'Data Analytics'" },
      { className: "accent", text: "'Signal Processing'" },
      { className: "accent", text: "'Real-time Apps'" },
      { className: "accent", text: "'WebSocket Systems'" },
      { className: "accent", text: "'Game Physics'" },
    ],
  },
  {
    command: "cat links.json",
    output: [
      { className: "dim", text: "{" },
      { className: "json-line", html: '  <span class="tl-key">"github"</span>:   <span class="tl-sep">"</span><span class="json-val-link" data-href="https://github.com/malleum/">github.com/malleum</span><span class="tl-sep">"</span>,' },
      { className: "json-line", html: '  <span class="tl-key">"linkedin"</span>: <span class="tl-sep">"</span><span class="json-val-link" data-href="https://www.linkedin.com/in/joshammer343/">linkedin.com/in/joshammer343</span><span class="tl-sep">"</span>,' },
      { className: "json-line", html: '  <span class="tl-key">"email"</span>:    <span class="tl-sep">"</span><span class="json-val-link" data-href="mailto:jph33@outlook.com">jph33@outlook.com</span><span class="tl-sep">"</span>' },
      { className: "dim", text: "}" },
    ],
  },
  {
    command: "echo $CONTACT",
    output: [{ className: "accent bold", text: "jph33@outlook.com" }],
  },
];

export const COMMANDS_BY_NAME = new Map(COMMANDS.map((entry) => [entry.command, entry]));
